/**
 * Governance Hub
 * 
 * Central dashboard for network governance (#260):
 * - Active proposals with signature progress
 * - Timelock countdowns
 * - Quick actions for common governance tasks
 * - PoVC proof badges
 * - Governance configuration display
 */

import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useDebug } from '../context/DebugContext';
import { useApprovals, useIdentities } from '../hooks/useHttpPolling';
import { SparkRenderer } from '../components/SparkRenderer';
import ProposalList, { Proposal } from '../components/ProposalList';
import './Governance.css';

// SVG Icons
const Icons = {
  Approvals: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 6L9 17l-5-5" />
      <rect x="3" y="3" width="18" height="18" rx="2" />
    </svg>
  ),
  KeyHolders: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="8" r="5" />
      <path d="M3 21c0-4.5 4-7 9-7s9 2.5 9 7" />
    </svg>
  ),
  Shield: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2l9 4v6c0 5.5-3.5 10-9 12-5.5-2-9-6.5-9-12V6l9-4z" />
    </svg>
  ),
  Clock: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  ),
  Activity: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </svg>
  ),
  ArrowRight: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 8h10M9 4l4 4-4 4" />
    </svg>
  ),
  Plus: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10 4v12M4 10h12" />
    </svg>
  ),
};

interface GovernanceConfig {
  normalThreshold: number;
  criticalThreshold: number;
  keyHolderCount: number;
  deployTimelock: number;
  upgradeTimelock: number;
}

// Tab options for the proposals view
type ProposalTab = 'active' | 'completed' | 'all';

export default function Governance() {
  const { addEvent } = useDebug();
  const { data: approvalsData, loading: approvalsLoading } = useApprovals();
  const { data: identitiesData, loading: identitiesLoading } = useIdentities();
  
  const [config, setConfig] = useState<GovernanceConfig | null>(null);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [showNewProposal, setShowNewProposal] = useState(false);
  const [activeTab, setActiveTab] = useState<ProposalTab>('active');
  
  const pendingCount = (approvalsData?.actions || []).filter(
    (a: any) => a.status === 'pending'
  ).length;
  
  const keyHolderCount = (identitiesData?.identities || []).filter(
    (i: any) => i.roles?.includes('key_holder')
  ).length || 5; // Default for display
  
  const loading = approvalsLoading || identitiesLoading;
  
  // Filter proposals based on active tab
  const filteredProposals = useMemo(() => {
    switch (activeTab) {
      case 'active':
        return proposals.filter(p => p.status === 'pending' || p.status === 'executing');
      case 'completed':
        return proposals.filter(p => ['approved', 'rejected', 'expired', 'completed'].includes(p.status));
      default:
        return proposals;
    }
  }, [proposals, activeTab]);
  
  useEffect(() => {
    addEvent({
      source: 'console',
      category: 'navigation',
      message: 'Viewed Active Proposals',
    });
    
    // Fetch governance config and proposals
    fetchGovernanceConfig();
    fetchProposals();
  }, [addEvent]);
  
  const fetchGovernanceConfig = async () => {
    try {
      const edgeUrl = import.meta.env.VITE_EDGE_URL || 'https://edge.estream.dev';
      const response = await fetch(`${edgeUrl}/api/governance/config`);
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      } else {
        // Default config for display
        setConfig({
          normalThreshold: 2,
          criticalThreshold: 4,
          keyHolderCount: 5,
          deployTimelock: 60,
          upgradeTimelock: 72,
        });
      }
    } catch {
      // Default config
      setConfig({
        normalThreshold: 2,
        criticalThreshold: 4,
        keyHolderCount: 5,
        deployTimelock: 60,
        upgradeTimelock: 72,
      });
    }
  };
  
  const fetchProposals = async () => {
    try {
      const edgeUrl = import.meta.env.VITE_EDGE_URL || 'https://edge.estream.dev';
      const response = await fetch(`${edgeUrl}/api/governance/list?limit=20`);
      if (response.ok) {
        const data = await response.json();
        setProposals((data.actions || []).map((a: any) => ({
          id: a.id,
          type: a.type || 'governance',
          action: a.action || `governance.${a.type}.deploy`,
          title: a.title || 'Untitled',
          description: a.description,
          status: a.status || 'pending',
          initiator: a.initiator || 'Unknown',
          createdAt: a.createdAt || new Date().toISOString(),
          expiresAt: a.expiresAt,
          timelockEndsAt: a.timelockEndsAt,
          threshold: a.threshold || 'normal',
          requiredSignatures: a.requiredSignatures || 2,
          currentSignatures: a.currentSignatures || 0,
          signatures: a.signatures || [],
          povcProof: a.povcProof,
        })));
      }
    } catch {
      // Mock data for development
      setProposals([
        {
          id: 'gov-1',
          type: 'circuit',
          action: 'governance.circuit.deploy',
          title: 'Deploy payment-processor v2.1.0',
          description: 'Production deployment of the updated payment processor circuit with enhanced throughput.',
          status: 'pending',
          initiator: 'alice@example.com',
          createdAt: new Date(Date.now() - 3600000).toISOString(),
          threshold: 'normal',
          requiredSignatures: 2,
          currentSignatures: 1,
          signatures: [
            { signer: 'alice@example.com', signedAt: new Date(Date.now() - 3500000).toISOString(), walletId: '0x1234...5678' },
          ],
        },
        {
          id: 'gov-2',
          type: 'config',
          action: 'governance.config.update',
          title: 'Update rate limits for API endpoints',
          status: 'pending',
          initiator: 'bob@example.com',
          createdAt: new Date(Date.now() - 7200000).toISOString(),
          threshold: 'normal',
          requiredSignatures: 2,
          currentSignatures: 0,
          signatures: [],
        },
        {
          id: 'gov-3',
          type: 'key',
          action: 'governance.key.add',
          title: 'Add new key holder: security-ops@acme.com',
          description: 'Adding security operations team member as a key holder for emergency response.',
          status: 'executing',
          initiator: 'charlie@example.com',
          createdAt: new Date(Date.now() - 86400000).toISOString(),
          timelockEndsAt: new Date(Date.now() + 3600000 * 24).toISOString(),
          threshold: 'critical',
          requiredSignatures: 4,
          currentSignatures: 4,
          signatures: [
            { signer: 'alice@example.com', signedAt: new Date(Date.now() - 85000000).toISOString(), walletId: '0x1234...5678' },
            { signer: 'bob@example.com', signedAt: new Date(Date.now() - 84000000).toISOString(), walletId: '0x2345...6789' },
            { signer: 'charlie@example.com', signedAt: new Date(Date.now() - 83000000).toISOString(), walletId: '0x3456...7890' },
            { signer: 'dave@example.com', signedAt: new Date(Date.now() - 82000000).toISOString(), walletId: '0x4567...8901' },
          ],
          povcProof: {
            hash: '0xabc123def456789012345678901234567890abcdef1234567890abcdef123456',
            timestamp: new Date(Date.now() - 80000000).toISOString(),
            verified: true,
          },
        },
        {
          id: 'gov-4',
          type: 'release',
          action: 'governance.release.deploy',
          title: 'Deploy lattice-runtime v1.5.2',
          status: 'approved',
          initiator: 'alice@example.com',
          createdAt: new Date(Date.now() - 172800000).toISOString(),
          threshold: 'normal',
          requiredSignatures: 2,
          currentSignatures: 2,
          signatures: [
            { signer: 'alice@example.com', signedAt: new Date(Date.now() - 172000000).toISOString(), walletId: '0x1234...5678' },
            { signer: 'bob@example.com', signedAt: new Date(Date.now() - 171000000).toISOString(), walletId: '0x2345...6789' },
          ],
          povcProof: {
            hash: '0xdef789abc123456789012345678901234567890abcdef1234567890abcdef789',
            timestamp: new Date(Date.now() - 170000000).toISOString(),
            verified: true,
          },
        },
        {
          id: 'gov-5',
          type: 'emergency',
          action: 'governance.emergency.halt',
          title: 'Emergency halt - suspected anomaly',
          status: 'rejected',
          initiator: 'eve@example.com',
          createdAt: new Date(Date.now() - 259200000).toISOString(),
          threshold: 'critical',
          requiredSignatures: 4,
          currentSignatures: 1,
          signatures: [
            { signer: 'eve@example.com', signedAt: new Date(Date.now() - 259000000).toISOString(), walletId: '0x5678...9012' },
          ],
        },
      ]);
    }
  };
  
  const handleSignProposal = (proposal: Proposal) => {
    addEvent({
      source: 'console',
      category: 'execution',
      message: `Signing proposal: ${proposal.title}`,
      data: { proposalId: proposal.id, action: proposal.action },
    });
  };

  const handleViewProposalDetails = (proposal: Proposal) => {
    addEvent({
      source: 'console',
      category: 'info',
      message: `Viewing proposal details: ${proposal.title}`,
      data: { proposalId: proposal.id },
    });
    // TODO: Navigate to proposal detail page or open modal
  };
  
  return (
    <div className="governance-hub">
      {/* Header */}
      <div className="hub-header">
        <div className="header-content">
          <h1>Active Proposals</h1>
          <p>Pending governance actions requiring multi-sig approval</p>
        </div>
        <button className="btn-primary" onClick={() => setShowNewProposal(true)}>
          <Icons.Plus /> New Proposal
        </button>
      </div>
      
      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon warning">
            <Icons.Approvals />
          </div>
          <div className="stat-content">
            <span className="stat-value">{loading ? '-' : pendingCount}</span>
            <span className="stat-label">Pending Approvals</span>
          </div>
        </div>
        
        <Link to="/governance/key-holders" className="stat-card clickable">
          <div className="stat-icon primary">
            <Icons.KeyHolders />
          </div>
          <div className="stat-content">
            <span className="stat-value">{loading ? '-' : keyHolderCount}</span>
            <span className="stat-label">Key Holders</span>
          </div>
          <span className="stat-arrow"><Icons.ArrowRight /></span>
        </Link>
        
        <div className="stat-card">
          <div className="stat-icon success">
            <Icons.Shield />
          </div>
          <div className="stat-content">
            <span className="stat-value">{config?.normalThreshold || 2}/{config?.criticalThreshold || 4}</span>
            <span className="stat-label">Normal / Critical Threshold</span>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon info">
            <Icons.Clock />
          </div>
          <div className="stat-content">
            <span className="stat-value">{config?.deployTimelock || 60}m</span>
            <span className="stat-label">Deploy Timelock</span>
          </div>
        </div>
      </div>
      
      {/* Proposals List */}
      <section className="proposals-section">
        <div className="proposals-header">
          <div className="proposals-tabs">
            <button 
              className={`tab-btn ${activeTab === 'active' ? 'active' : ''}`}
              onClick={() => setActiveTab('active')}
            >
              Active
              {proposals.filter(p => p.status === 'pending' || p.status === 'executing').length > 0 && (
                <span className="tab-count">
                  {proposals.filter(p => p.status === 'pending' || p.status === 'executing').length}
                </span>
              )}
            </button>
            <button 
              className={`tab-btn ${activeTab === 'completed' ? 'active' : ''}`}
              onClick={() => setActiveTab('completed')}
            >
              Completed
            </button>
            <button 
              className={`tab-btn ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              All
            </button>
          </div>
          <Link to="/governance/activity" className="view-activity">
            <Icons.Activity /> View Audit Log
          </Link>
        </div>
        
        <ProposalList
          proposals={filteredProposals}
          normalThreshold={config?.normalThreshold || 2}
          criticalThreshold={config?.criticalThreshold || 4}
          keyHolderCount={keyHolderCount}
          onSign={handleSignProposal}
          onViewDetails={handleViewProposalDetails}
          loading={loading}
        />
      </section>
      
      {/* Main Content Grid */}
      <div className="hub-content">
        
        {/* Governance Configuration */}
        <section className="card config-card">
          <div className="card-header">
            <h2>
              <Icons.Shield />
              Configuration
            </h2>
          </div>
          
          <div className="config-grid">
            <div className="config-item">
              <span className="config-label">Normal Threshold</span>
              <span className="config-value">{config?.normalThreshold || 2} of {keyHolderCount}</span>
              <span className="config-desc">Required for routine operations</span>
            </div>
            
            <div className="config-item">
              <span className="config-label">Critical Threshold</span>
              <span className="config-value">{config?.criticalThreshold || 4} of {keyHolderCount}</span>
              <span className="config-desc">Required for upgrades, key rotation</span>
            </div>
            
            <div className="config-item">
              <span className="config-label">Deploy Timelock</span>
              <span className="config-value">{config?.deployTimelock || 60} minutes</span>
              <span className="config-desc">Delay before deployments execute</span>
            </div>
            
            <div className="config-item">
              <span className="config-label">Upgrade Timelock</span>
              <span className="config-value">{config?.upgradeTimelock || 72} hours</span>
              <span className="config-desc">Delay before protocol upgrades</span>
            </div>
          </div>
          
          <div className="config-actions">
            <button className="btn-secondary">View Full Config</button>
            <button className="btn-secondary">Propose Changes</button>
          </div>
        </section>
        
        {/* Quick Actions */}
        <section className="card actions-card">
          <div className="card-header">
            <h2>Quick Actions</h2>
          </div>
          
          <div className="quick-actions">
            <button className="action-btn">
              <span className="action-icon">⚡</span>
              <span className="action-text">
                <span className="action-title">Deploy Circuit</span>
                <span className="action-desc">Submit a new circuit deployment</span>
              </span>
            </button>
            
            <button className="action-btn">
              <span className="action-icon">🔑</span>
              <span className="action-text">
                <span className="action-title">Add Key Holder</span>
                <span className="action-desc">Propose adding a new signer</span>
              </span>
            </button>
            
            <button className="action-btn">
              <span className="action-icon">⚙️</span>
              <span className="action-text">
                <span className="action-title">Update Config</span>
                <span className="action-desc">Propose configuration changes</span>
              </span>
            </button>
            
            <button className="action-btn danger">
              <span className="action-icon">🚨</span>
              <span className="action-text">
                <span className="action-title">Emergency Halt</span>
                <span className="action-desc">Initiate emergency shutdown</span>
              </span>
            </button>
          </div>
        </section>
      </div>
      
      {/* New Proposal Modal */}
      {showNewProposal && (
        <NewProposalModal onClose={() => setShowNewProposal(false)} />
      )}
    </div>
  );
}

// New Proposal Modal
interface NewProposalModalProps {
  onClose: () => void;
}

function NewProposalModal({ onClose }: NewProposalModalProps) {
  const [proposalType, setProposalType] = useState<'circuit' | 'config' | 'identity'>('circuit');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sparkChallenge, setSparkChallenge] = useState<string | null>(null);
  
  const handleSubmit = async () => {
    if (!title.trim()) return;
    
    setSubmitting(true);
    
    try {
      const edgeUrl = import.meta.env.VITE_EDGE_URL || 'https://edge.estream.dev';
      const response = await fetch(`${edgeUrl}/api/governance/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: proposalType,
          title,
          description,
          initiator: 'console-operator',
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        // Generate Spark challenge for signing
        const challenge = JSON.stringify({
          challenge_id: data.id || `proposal-${Date.now()}`,
          session_id: `session-${Date.now()}`,
          display_code: Math.random().toString(36).substring(2, 10).toUpperCase(),
          expires_at: Date.now() + 300000,
          payload: btoa(JSON.stringify({ proposal_id: data.id, type: proposalType })),
        });
        setSparkChallenge(challenge);
      }
    } catch (error) {
      console.error('Failed to create proposal:', error);
      // Mock challenge for development
      setSparkChallenge(JSON.stringify({
        challenge_id: `mock-${Date.now()}`,
        session_id: `session-${Date.now()}`,
        display_code: 'MOCK1234',
        expires_at: Date.now() + 300000,
      }));
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="proposal-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>New Governance Proposal</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        {sparkChallenge ? (
          <div className="spark-signing">
            <p>Scan with your Seeker app to sign this proposal:</p>
            <SparkRenderer
              challengeJson={sparkChallenge}
              variant="genesis"
              size={260}
              showCode
            />
            <button className="btn-secondary" onClick={onClose}>Done</button>
          </div>
        ) : (
          <>
            <div className="modal-body">
              <div className="form-group">
                <label>Proposal Type</label>
                <div className="type-selector">
                  <button 
                    className={`type-btn ${proposalType === 'circuit' ? 'active' : ''}`}
                    onClick={() => setProposalType('circuit')}
                  >
                    Circuit
                  </button>
                  <button 
                    className={`type-btn ${proposalType === 'config' ? 'active' : ''}`}
                    onClick={() => setProposalType('config')}
                  >
                    Config
                  </button>
                  <button 
                    className={`type-btn ${proposalType === 'identity' ? 'active' : ''}`}
                    onClick={() => setProposalType('identity')}
                  >
                    Identity
                  </button>
                </div>
              </div>
              
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="e.g., Deploy payment-processor v2.1.0"
                />
              </div>
              
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="Describe the proposed change..."
                  rows={4}
                />
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="btn-secondary" onClick={onClose}>Cancel</button>
              <button 
                className="btn-primary" 
                onClick={handleSubmit}
                disabled={!title.trim() || submitting}
              >
                {submitting ? 'Creating...' : 'Create Proposal'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
